import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import zionDebug from '@fs/zion-debug'
import ZionForm, { useFieldArray, useFormState, useZionForm } from '@fs/zion-form'
import { ContentAdd } from '@fs/zion-icon'
import {
  getPersonRelationships,
  useSlsStateContext,
  generateSls,
  getImage,
  getNestedOrFloatingElementById,
  getPersonName,
  useSlsHighlightsContext,
  useSlsDispatchContext,
} from '@fs/zion-sls'
import { BillboardButton, InfoSheetPanel, Separator, useOverlay } from '@fs/zion-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import { getNextUuid } from '../../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import useOthersOnThisRecord from '../../hooks/useOthersOnThisRecord'
import useR2RelationshipOptions from '../../hooks/useR2RelationshipOptions'
import useSetRecordHighlightsOfOneRecord from '../../hooks/useSetRecordHighlightsOfOneRecord'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import ChooseRecordOverlay from './ChooseRecordOverlay/ChooseRecordOverlay'
import PanelHeader from './PanelHeader'
import SaveComponent from '../../../../components/generic/panels/shared/SaveComponent'
import { submitEditRelationships } from './utils/editPanelDispatchBuilders'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { R2AppName } from '../../../../../shared/services/config'
import OthersOnRecordCard from './editRelationships/components/OthersOnRecordCard'
import RelationshipField from './editRelationships/components/RelationshipField'
import RelationshipTips from './editRelationships/components/RelationshipTips'
import {
  getNewLabel,
  collectHighlightIdsForRelationships,
  findNewlyAddedChips,
  findRemovedRelationships,
  createRelationshipFromChip,
  createRelationshipValidationShape,
  getRelationshipChipData,
  getEditRelationshipDefaults,
} from './editRelationships/utils/EditRelationships.utls'
import { usePersonOverlaySelection } from './editRelationships/hooks/usePersonOverlaySelection'

const debug = zionDebug('rip:R2:EditRelationships')

/** Relationship between the words "primary", and "principal": Code uses the term Primary, but the user sees the term Principal  */

const EditRelationshipsForm = ({
  personIdOverlay,
  setPersonIdOverlay,
  selectedPerson,
  currentPersonRecord,
  relationshipOptions,
}) => {
  const state = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [nameFieldIds, setNameFieldIds] = useState([])
  const [movingPersonToOtherRecord, setMovingPersonToOtherRecord] = useState(false)
  const [warningPerson, setWarningPerson] = useState()
  const [otherPerson, setOtherPerson] = useState()
  const [warningId, setWarningId] = useState('')
  const [record1Relationships, setRecord1Relationships] = useState([])
  const [record2Relationships, setRecord2Relationships] = useState([])
  const overlayField = useRef(null)
  const [t] = useTranslation()
  const relationshipSelectorOverlay = useOverlay()
  const chooseRecordOverlay = useOverlay()
  const setRecordHighlightsOfOneRecord = useSetRecordHighlightsOfOneRecord()
  const { setDisplayHighlights, getHighlights } = useSlsHighlightsContext()
  const { editRelationshipsOverlay, editHighlightsOverlay, closeOverlay, topMostPanel, addPersonGenericOverlay } =
    useOverlayContext()
  const saveChangesOverlay = useOverlay()
  const [changesMade, setChangesMade] = useState(false)
  const slsRelationships = useMemo(
    () => getPersonRelationships(selectedPerson, state.images),
    [selectedPerson, state.images]
  )
  const formDefaultValues = useMemo(
    () => getEditRelationshipDefaults(selectedPerson, relationshipOptions, slsRelationships),
    [selectedPerson, relationshipOptions, slsRelationships]
  )
  const shape = createRelationshipValidationShape(t)

  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
  })

  // Using formState from useFormState instead of methods.formState because we want the most up to date formState
  // in our submit handler without having to worry about it being stale
  const formState = useFormState({ control: methods.control })

  const fieldArrayMethods = useFieldArray({
    name: 'relationships',
    control: methods.control,
    keyName: 'formId', // to avoid conflict with 'id' from our data
  })
  const { fields, remove, append, update } = fieldArrayMethods

  const othersOnThisRecord = useOthersOnThisRecord(
    currentPersonRecord,
    slsRelationships,
    state.currentImageArk,
    thumbnails,
    r2State.selectedPersonId
  )

  const relationshipChipData = getRelationshipChipData(t, state)

  if (typeof relationshipChipData[2].data === 'object') {
    relationshipChipData[2].data.push({
      label: t('edit-relationships.chips.other', 'Other'),
      relType: 'OTHER',
      value: 'Other',
    })
  }

  const temporarySlsPersons = useMemo(() => {
    const tempRelationshipInfo = {}
    Object.values(r2State.temporaryPersonsDataForRelationships || {}).forEach((data) => {
      if (data?.slsPerson) {
        tempRelationshipInfo[data.personId] = data.slsPerson
      }
    })
    return tempRelationshipInfo
  }, [r2State.temporaryPersonsDataForRelationships])

  useUnsavedChangesOverlay({
    debug,
    debugMessage: 'Relationships subscriber called: ',
    methods,
    overlay: saveChangesOverlay,
  })

  // Reset the add person panel args and newly added persons data
  const resetR2Context = useCallback(() => {
    r2Dispatch({
      type: 'BATCH_DISPATCHES',
      value: [
        {
          type: 'SET_ADD_PERSON_PANEL_ARGS',
          value: {
            referrer: null,
            recordId: null,
            metadata: {},
          },
        },
        {
          type: 'CLEAR_TEMPORARY_PERSONS_DATA_FOR_RELATIONSHIPS',
        },
      ],
    })
  }, [r2Dispatch])

  const resetAndClose = useCallback(() => {
    // If there are newly added persons that haven't been saved yet, remove them from the SLS context
    const allNewPersonIds = Object.values(r2State.temporaryPersonsDataForRelationships || {}).map(
      (data) => data?.personId
    )

    const removeDispatches = allNewPersonIds.map((personId) => ({
      type: 'REMOVE_ELEMENT',
      value: { id: personId, elementType: 'PERSON', partitionKey: state.currentImageArk },
    }))

    if (allNewPersonIds.length > 0) {
      // Remove the newly added person if the user cancels out of the edit relationships panel.
      // Since they haven't saved yet we just remove the person from the SLS context.
      zionSlsDispatch({
        type: 'BATCH_DISPATCHES',
        value: removeDispatches,
      })
      resetR2Context()
    }

    closeOverlay('editRelationships')
  }, [
    zionSlsDispatch,
    r2State.temporaryPersonsDataForRelationships,
    state.currentImageArk,
    closeOverlay,
    resetR2Context,
  ])

  // The back arrow on the panel was clicked
  const userClosedPanel = useCallback(() => {
    if (formState.isDirty) saveChangesOverlay.open()
    else {
      resetAndClose()
    }
  }, [formState.isDirty, saveChangesOverlay, resetAndClose])

  const handleSubmit = (formValues) => {
    const relationships = formValues?.relationships || {}
    // Get all selected person Ids from the relationships fields
    const selectedPersonIds = Object.values(relationships)
      .map((field) => field?.value?.key)
      .filter(Boolean)

    // We just need to get all dispatches related to newly added persons that are being referenced in relationships
    // If a temporary person is not being referenced in a relationship, we don't need to save them to SLS
    const allNewPersonsDispatches = Object.values(r2State.temporaryPersonsDataForRelationships || {})
      .filter((data) => selectedPersonIds.includes(data?.personId))
      .flatMap((data) => data?.dispatches?.fullArray || [])

    const relationshipDispatches = submitEditRelationships({
      ...state,
      ...r2State,
      thumbnails,
      values: formValues,
      formState,
      movingPersonToOtherRecord,
      temporarySlsPersons,
    })

    const allDispatches = [...allNewPersonsDispatches, ...relationshipDispatches]

    return { sls: generateSls(allDispatches), dispatches: allDispatches }
  }

  const finished = () => {
    resetR2Context()
    closeOverlay('editRelationships')
  }

  const highlightRelationshipFields = useCallback(() => {
    const values = methods.getValues()
    const highlightsObj = getHighlights()
    const { allPeopleHighlights, allPeopleReferenceHighlights } = collectHighlightIdsForRelationships(
      values,
      selectedPerson?.id,
      highlightsObj.mainEditHighlights,
      highlightsObj.splitScreenEditHighlights
    )
    const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark
    setDisplayHighlights({
      highlights: [...allPeopleHighlights, ...allPeopleReferenceHighlights],
      mainPartitionKey: state.currentImageArk,
      splitScreenPartitionKey: splitScreenImageArk,
      fieldTypes: ['NAME_SURN', 'NAME_SUF', 'NAME_GN'],
      customFilter: (h) => h.personIds.length > 0,
    })
    setRecordHighlightsOfOneRecord()
  }, [
    getHighlights,
    setDisplayHighlights,
    methods,
    r2State.referenceFocusedIndex,
    selectedPerson?.id,
    setRecordHighlightsOfOneRecord,
    state.currentImageArk,
    thumbnails,
  ])

  const handleRelationshipChange = (relationshipField) => {
    // if moving a person to another record, we need to delete their relationships
    // User chooses which person has relationships deleted
    const index = fields.findIndex((rf) => rf.id === relationshipField.id)
    if (index === -1) {
      return
    }
    const autosuggestValue = methods.getValues(`relationships[${index}].value`)
    const othrPerson =
      temporarySlsPersons?.[autosuggestValue?.key] ||
      getNestedOrFloatingElementById(state.images, autosuggestValue?.key)
    // Check for a null value
    if (autosuggestValue?.key) {
      const otherPersonRecordId = othrPerson.recordId

      if (otherPersonRecordId && selectedPerson.recordId !== otherPersonRecordId) {
        const record1Rels = getPersonRelationships(selectedPerson, state.images)
        const record2Rels = getPersonRelationships(othrPerson, state.images)
        setWarningPerson(selectedPerson)
        setOtherPerson(othrPerson)
        setRecord1Relationships(record1Rels)
        setRecord2Relationships(record2Rels)
        setWarningId(relationshipField.id)
        chooseRecordOverlay.handleClick()
      }
    }
    // Correct gendered terms in label to match new person
    const newLabel = getNewLabel({ relationshipField, otherPerson: othrPerson, state })
    if (newLabel) {
      update(index, { ...relationshipField, label: newLabel, value: autosuggestValue })
    }
  }

  const handleChipSelection = (chips) => {
    const removedRelationships = findRemovedRelationships(fields, chips)
    removedRelationships.reverse() // Remove from the end of the array to avoid index shifting

    removedRelationships.forEach((rel) => {
      const index = fields.findIndex((rf) => rf.id === rel.id)
      remove(index)
      setChangesMade(true)
    })

    const newChips = findNewlyAddedChips(chips)
    if (newChips.length > 0) {
      newChips.reverse()
      newChips.forEach((chip) => {
        const newRelationship = createRelationshipFromChip(chip, getNextUuid())
        append(newRelationship)
      })
    }
  }

  usePersonOverlaySelection({
    personIdOverlay,
    setPersonIdOverlay,
    addPersonPanelOverlay: addPersonGenericOverlay,
    relationshipOptions,
    fields,
    overlayField,
    methods,
    handleRelationshipChange,
    setChangesMade,
  })

  useEffect(() => {
    if (editRelationshipsOverlay.isOpen && topMostPanel() === 'editRelationships' && !editHighlightsOverlay.isOpen) {
      highlightRelationshipFields()
    }
  }, [
    editHighlightsOverlay.isOpen,
    editRelationshipsOverlay.isOpen,
    nameFieldIds,
    topMostPanel,
    fields,
    highlightRelationshipFields,
  ])

  // Hook for handling person overlay selection - we don't need to capture the overlayField
  // as it's managed internally by the hook using the useOverlayContext

  const heading = t('edit-relationships.heading', 'Relationships')
  const instructions = t(
    'edit-relationships.instructions',
    'Fix any relationships that are incorrect. Add or remove fields as necessary.'
  )
  const addMoreFields = t('edit-index.add-more-info.button', 'Add More Information', { field: heading })

  debug('Render: ', { selectedPerson, fields })

  return (
    <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-relationships">
      <InfoSheetPanel
        {...{ ...editRelationshipsOverlay, close: userClosedPanel }}
        headingColor="green"
        title={t('edit-index.edit-person.heading', 'Edit {personName}', {
          personName: getPersonName({ person: selectedPerson }),
        })}
        footer={
          <SaveComponent
            saveChangesOverlay={saveChangesOverlay}
            onSubmit={handleSubmit}
            finished={finished}
            canceled={resetAndClose}
            appName={R2AppName}
            personId={r2State.userId}
            formName="edit-relationships"
            disableSave={!changesMade}
            saveInitiatorFilename="EditRelationships.js"
          />
        }
      >
        <PanelHeader heading={heading} caption={instructions} headingSize="H5" />
        <Separator />
        <RelationshipTips />
        <div>
          {fields?.map((field, index) => {
            // In case the person is on an image besides this one

            return (
              <RelationshipField
                key={field.id}
                relationshipField={field}
                relationshipOptions={relationshipOptions}
                methods={methods}
                handleRelationshipChange={handleRelationshipChange}
                movingPersonToOtherRecord={movingPersonToOtherRecord}
                otherPerson={otherPerson}
                setChangesMade={setChangesMade}
                highlightRelationshipFields={highlightRelationshipFields}
                index={index}
                setNameFieldIds={setNameFieldIds}
                overlayField={overlayField}
                temporarySlsPersons={temporarySlsPersons}
              />
            )
          })}
          <BillboardButton
            data-testid="add-or-remove-fields"
            Icon={ContentAdd}
            color="yellow"
            emphasis="low"
            disabled={movingPersonToOtherRecord}
            onClick={relationshipSelectorOverlay.handleClick}
          >
            {addMoreFields}
          </BillboardButton>
        </div>
        {othersOnThisRecord.length > 0 && <OthersOnRecordCard othersOnThisRecord={othersOnThisRecord} state={state} />}
        <FieldSelectorOverlay
          title={addMoreFields}
          lockSharedFields={false} // Relationships have a shared field, but it is meant to be that way.  We don't want to lock the relationship fields  --Talk to Patrick
          chipData={relationshipChipData}
          fieldData={fields}
          handleSelection={handleChipSelection}
          overlay={relationshipSelectorOverlay}
        />
        {warningPerson && (
          <ChooseRecordOverlay
            overlay={chooseRecordOverlay}
            person={warningPerson}
            otherPerson={otherPerson}
            record1Relationships={record1Relationships}
            record2Relationships={record2Relationships}
            setMovingPersonToOtherRecord={setMovingPersonToOtherRecord}
            onCancel={() => {
              methods.resetField(warningId)
              highlightRelationshipFields() // We Need to reset the highlights if the user cancels the move
            }}
          />
        )}
      </InfoSheetPanel>
    </ZionForm>
  )
}

const EditRelationships = ({ personIdOverlay, setPersonIdOverlay }) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const currentImage = getImage(state?.images, state?.currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State.selectedPersonId)
  let currentPersonRecord = getNestedOrFloatingElementById([currentImage], selectedPerson?.recordId)
  if (!currentPersonRecord && selectedPerson?.superElements?.length > 0) {
    const recordImage = getImage(state?.images, selectedPerson.superElements[0].partitionKey)
    currentPersonRecord = getNestedOrFloatingElementById([recordImage], selectedPerson?.recordId)
  }

  const baseRelationshipOptions = useR2RelationshipOptions({
    selectedPerson,
    currentPersonRecord,
    referenceFocusedIndex: r2State.referenceFocusedIndex,
    splitScreen: r2State.splitScreen,
  })

  const temporaryPersonOptions = useMemo(() => {
    const slsPersons = Object.values(r2State.temporaryPersonsDataForRelationships || {}).map((data) => data?.slsPerson)
    return slsPersons.map((person) => ({
      key: person.id,
      primaryText: getPersonName({ person }),
      group: t('edit-relationships.autosuggest.temporary-persons', 'TEMPORARY PERSONS'),
    }))
  }, [r2State.temporaryPersonsDataForRelationships, t])

  const relationshipOptions = useMemo(
    () => [...temporaryPersonOptions, ...baseRelationshipOptions],
    [baseRelationshipOptions, temporaryPersonOptions]
  )

  if (relationshipOptions && relationshipOptions.length < 1) return null
  return (
    <EditRelationshipsForm
      personIdOverlay={personIdOverlay}
      setPersonIdOverlay={setPersonIdOverlay}
      selectedPerson={selectedPerson}
      currentPersonRecord={currentPersonRecord}
      relationshipOptions={relationshipOptions}
    />
  )
}

export default EditRelationships
